import { create, StateCreator } from "zustand";
import { persist } from "zustand/middleware";

import {
  DEFAULT_THEME_ID,
  isThemeId,
  type ThemeId,
} from "@/features/settings/theme/themeOptions";

interface UIState {
  sidebarCollapsed: boolean;
  expandedMenuItems: string[];
  theme: ThemeId;
}

interface UIActions {
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleMenuItem: (itemId: string) => void;
  setTheme: (theme: UIState["theme"]) => void;
  reset: () => void;
}

const initialState: UIState = {
  sidebarCollapsed: false,
  expandedMenuItems: ["ventas"],
  theme: DEFAULT_THEME_ID,
};

type UIStore = UIState & UIActions;

const storeCreator: StateCreator<UIStore> = (set) => ({
  ...initialState,

  toggleSidebar: () =>
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  setSidebarCollapsed: (collapsed: boolean) =>
    set({ sidebarCollapsed: collapsed }),

  toggleMenuItem: (itemId: string) =>
    set((state) => ({
      expandedMenuItems: state.expandedMenuItems.includes(itemId)
        ? state.expandedMenuItems.filter((id) => id !== itemId)
        : [...state.expandedMenuItems, itemId],
    })),

  setTheme: (theme: UIState["theme"]) => set({ theme }),

  reset: () => set(initialState),
});

export const useUIStore = create<UIStore>()(
  persist(storeCreator, {
    name: "farmacia-vita-ui",
    version: 1,
    // NOTE: persist key keeps the legacy `farmacia-vita-ui` name to avoid
    // breaking existing users; stored `light`/`dark`/`system` values from the
    // previous theme model fall back to `DEFAULT_THEME_ID` below.
    migrate: (persistedState) => {
      const persisted = { ...((persistedState ?? {}) as Record<string, unknown>) };
      if (!isThemeId(persisted.theme)) {
        delete persisted.theme;
      }
      return persisted as unknown as UIState;
    },
    merge: (persisted, current) => {
      const incoming = (persisted ?? {}) as Partial<UIState>;
      return {
        ...current,
        ...incoming,
        theme: isThemeId(incoming.theme)
          ? incoming.theme
          : DEFAULT_THEME_ID,
      };
    },
    partialize: (state: UIStore) => ({
      sidebarCollapsed: state.sidebarCollapsed,
      expandedMenuItems: state.expandedMenuItems,
      theme: state.theme,
    }),
  })
);
